import { useRef, useEffect } from 'react'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Container from '@/components/container'
import LinkComponent from '@/components/link-component'
import SelectLocale from '@/components/select-locale'
import CtaLink from '@/components/ui/cta-link'
import RichText from '@/components/ui/rich-text'
import { cx } from '@/utils/strings'
import routes from '@/constants/routes'
import Newsletter from './newsletter'

import ThemeSwitcher from './ui/theme-switcher'
import { TypeSectionFooter } from '@/types/ctf'

type FooterProps = {
  footerNav: TypeSectionFooter<'WITHOUT_UNRESOLVABLE_LINKS'>
  newsletterForm?: any
  showLinkSections?: boolean
  otherLocales?: { label: string; code: string; url: string }[]
}

export default function Footer({ footerNav, newsletterForm, showLinkSections = true, otherLocales = [] }: FooterProps) {
  if (!footerNav) return <></>
  const footerBottomLinksRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const consentLink =
      footerBottomLinksRef?.current?.querySelector('a[href$="#consent"]') ||
      footerBottomLinksRef?.current?.querySelector('a[href$="#osano"]')

    if (consentLink) {
      consentLink.addEventListener('click', (event) => {
        if (window.ketch) {
          event.preventDefault()
          window.ketch('showPreferences')
        }
      })
    }
  }, [])

  const {
    fields: { linkSections, bottomText },
  } = footerNav

  const footerIconStyle =
    'text-base border-2 border-current rounded-full size-10 hover:text-primaryBlue dark:hover:text-secondary-300 flex items-center justify-center [&_span.font-bwi]:text-[1.2rem]'
  const footerLinkStyle =
    'text-base! xl:text-sm! text-secondary-800 hover:text-primaryBlue dark:text-secondary-200 dark:hover:text-secondary-300'

  const renderBottomLinks = () => (
    <div
      ref={footerBottomLinksRef}
      className="text-center lg:text-left [&_p]:text-base [&_p]:mb-0 mb-0 [&>p>a]:no-underline [&>p>a]:ml-1 [&>p>a]:text-secondary-800 [&>p>a:hover]:text-primaryBlue dark:[&>p>a]:text-secondary-200 dark:[&>p>a:hover]:text-secondary-300"
    >
      {bottomText && <RichText body={bottomText.fields.body} />}
    </div>
  )

  return (
    <footer
      className={cx(
        'relative py-10',
        showLinkSections && 'bg-linear-[305.95deg] from-bw-teal-light-highlight/0 to-primary-500/15',
        showLinkSections && 'dark:bg-linear-[355.75deg] dark:from-primaryBlue/0 dark:to-primaryBlue/20',
        !showLinkSections &&
          'bg-secondary-50 border-t border-secondary-100 dark:bg-secondary-900 dark:border-secondary-900'
      )}
    >
      <Container>
        {showLinkSections ? (
          <div className="flex flex-col gap-10">
            <div className="flex flex-col 2xl:flex-row gap-10">
              <div className="flex-1 max-w-[50rem]">
                <div className="w-48 mb-6 2xl:mb-20">
                  <LinkComponent to={routes.index}>
                    <LogoHorizontal className="w-full text-primaryBlue dark:text-white" />
                  </LinkComponent>
                </div>
                {newsletterForm && <Newsletter form={newsletterForm.fields} size="Small" variant="Plain" />}
              </div>
              <nav className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {linkSections?.map(
                  (section) =>
                    section && (
                      <div key={`section-${section.sys?.id || section.fields.heading}`}>
                        <h4 className="text-lg py-0 mb-4">{section.fields.heading}</h4>
                        <ul className="flex flex-col gap-3 list-none">
                          {section.fields.links?.map(
                            (link) =>
                              link && (
                                <li key={link.sys.id}>
                                  <CtaLink
                                    to={link.fields.link?.fields.url}
                                    className={cx(footerLinkStyle)}
                                    size="Smaller"
                                    style="Text Link"
                                  >
                                    {link.fields.label}
                                  </CtaLink>
                                </li>
                              )
                          )}
                        </ul>
                      </div>
                    )
                )}
              </nav>
            </div>
            <div className="flex flex-col max-lg:gap-6 gap-4 xl:gap-6 justify-between items-center lg:flex-row [&>p]:text-base">
              {renderBottomLinks()}
              <div className="flex gap-3 xl:gap-4 flex-wrap">
                <LinkComponent to="https://twitter.com/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-twitter">
                    &#xe9a5;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://reddit.com/r/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-reddit">
                    &#xe95a;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://community.bitwarden.com/" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-community">
                    &#xe994;
                  </span>
                </LinkComponent>
                <LinkComponent to="https://github.com/bitwarden" className={cx(footerIconStyle, footerLinkStyle)}>
                  <span className="font-bwi" data-name="bwi-github">
                    &#xe950;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.youtube.com/channel/UCId9a_jQqvJre0_dE2lE_Rw"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-youtube">
                    &#xe966;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.linkedin.com/company/bitwarden1"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-linkedin">
                    &#xe955;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.facebook.com/bitwarden/"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-facebook">
                    &#xe94d;
                  </span>
                </LinkComponent>
                <LinkComponent
                  to="https://www.instagram.com/bitwarden/"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-instagram">
                    &#xe999;
                  </span>
                </LinkComponent>
                <LinkComponent
                  rel="me"
                  to="https://fosstodon.org/@bitwarden"
                  className={cx(footerIconStyle, footerLinkStyle)}
                >
                  <span className="font-bwi" data-name="bwi-mastodon">
                    &#xe995;
                  </span>
                </LinkComponent>
              </div>
              <div>
                <SelectLocale otherLocales={otherLocales as never[]} />
              </div>
              <ThemeSwitcher />
            </div>
          </div>
        ) : (
          <div className="flex justify-between">
            <div className="w-40">
              <LinkComponent to={routes.index}>
                <LogoHorizontal className="w-full text-primaryBlue dark:text-white" />
              </LinkComponent>
            </div>
            <div className="flex gap-4 items-center">
              {renderBottomLinks()}
              <ThemeSwitcher />
            </div>
          </div>
        )}
      </Container>
    </footer>
  )
}
